import {
  createContext,
  useContext,
  useState,
  useCallback,
  type PropsWithChildren,
} from 'react';
import type { TodoListState, TodoStatus } from './types';

interface TodoItemPatch {
  id: string;
  label?: string;
  status?: TodoStatus;
  detail?: string;
}

interface TodoListContextData {
  todoList: TodoListState | null;
  setTodoList: (state: TodoListState) => void;
  updateTodoItems: (patches: TodoItemPatch[]) => void;
  clearTodoList: () => void;
}

const TodoListContext = createContext<TodoListContextData | undefined>(
  undefined,
);

export function TodoListProvider({ children }: PropsWithChildren) {
  const [todoList, setTodoListState] = useState<TodoListState | null>(null);

  const setTodoList = useCallback((state: TodoListState) => {
    setTodoListState(state);
  }, []);

  const updateTodoItems = useCallback((patches: TodoItemPatch[]) => {
    setTodoListState((prev) => {
      if (!prev) return prev;
      const patchMap = new Map(patches.map((p) => [p.id, p]));
      return {
        ...prev,
        items: prev.items.map((item) => {
          const patch = patchMap.get(item.id);
          if (!patch) return item;
          return {
            ...item,
            ...(patch.label !== undefined && { label: patch.label }),
            ...(patch.status !== undefined && { status: patch.status }),
            ...(patch.detail !== undefined && { detail: patch.detail }),
          };
        }),
      };
    });
  }, []);

  const clearTodoList = useCallback(() => {
    setTodoListState(null);
  }, []);

  return (
    <TodoListContext.Provider
      value={{ todoList, setTodoList, updateTodoItems, clearTodoList }}
    >
      {children}
    </TodoListContext.Provider>
  );
}

export function useTodoList() {
  const context = useContext(TodoListContext);
  if (context === undefined) {
    throw new Error('useTodoList must be used within a TodoListProvider');
  }
  return context;
}
